/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import React, { useEffect, useState, useContext } from 'react';
import { useSiteData } from 'react-static';
import { ThemeContext } from 'styled-components';
import {
  autoFormatPhone,
  isEmpty,
  isPhoneNumber,
} from '@nmx/utils-frontend/dist/utilities/validators';
// template components
import Button from '@nmx/template/dist/componentLibrary/components/Button';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Loading from '@nmx/template/dist/componentLibrary/components/Loading';
import RadioGroup from '@nmx/template/dist/componentLibrary/components/forms/RadioGroup';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Select from '@nmx/template/dist/componentLibrary/components/forms/Select';
import SubmissionError from '@nmx/template/dist/componentLibrary/modules/forms/SubmissionError';
import SubmissionSuccess from '@nmx/template/dist/componentLibrary/modules/forms/SubmissionSuccess';
import TextField from '@nmx/template/dist/componentLibrary/components/forms/TextField';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// native components
import ClientServicesHours7to5 from '../../../components/clientServicesHours7to5';
// services
import AjaxForm from '../../../../services/ajaxForm.service';
import {
  validateDateField,
  validateEmailField,
  validateNameField,
  validatePhoneNumberField,
  validateTextField,
  validateZipField,
} from '../../utils/customValidators';
// utils
import { stateOptionsList } from '../../../../utils/stateOptionsList';
import { getCurrentDateMachineFormat } from '../../utils/getCurrentDate';
// helper functions
import { errorCheck, setErrorMessage } from '../../utils/helperFunctions';
// regex
import { trimStringRegex } from '../../../../utils/globalRegex';
// claims form styles
import {
  AddressStyled,
  BulletedListStyled,
  ColStyled,
  FormStyled,
  LabelStyled,
  LegendStyled,
  LinkStyled,
  TextAreaStyled,
} from '../../styles';
// constants
const CITY_NAME_MAX_LENGTH = 14;
const DATE_MAX_LENGTH = 10;
const DATE_MUST_BE_PRIOR_DATE = true;
const EMAIL_MAX_LENGTH = 254;
const FORM_NAME = 'Notice of long term care claim form';
const FREE_FORM_NAME_FIELD = 70;
const INSURED = 'Insured';
const INSUREDS_FR = 'Financial Representative/AFR';
const INSUREDS_REPRESENTATIVE = "Insured's Representative";
const LAST_NAME_FIELD_MAX_LENGTH = 30;
const MAILING_ADDRESS_MAX_LENGTH = 35;
const NAME_FIELD_MAX_LENGTH = 20;
const NO = 'No';
const PHONE_MAX_LENGTH = 14;
const REQUIRED = true;
const YES = 'Yes';
const ZIP_MAX_LENGTH = 5;

export default () => {

  // ░█████╗░███╗░░██╗// ██╗░░░░░░█████╗░░█████╗░██████╗░
  // ██╔══██╗████╗░██║// ██║░░░░░██╔══██╗██╔══██╗██╔══██╗
  // ██║░░██║██╔██╗██║// ██║░░░░░██║░░██║███████║██║░░██║
  // ██║░░██║██║╚████║// ██║░░░░░██║░░██║██╔══██║██║░░██║
  // ╚█████╔╝██║░╚███║// ███████╗╚█████╔╝██║░░██║██████╔╝
  // ░╚════╝░╚═╝░░╚══╝// ╚══════╝░╚════╝░╚═╝░░╚═╝╚═════╝░

  useEffect(() => {
  // adobe datalayer push
    window.adobeDataLayer.push({
      event: 'form-load',
      form:{
        name : FORM_NAME,
        experience: 'other',
        'form-type': 'simple',
      },
    });
  }, []);

  const currentDate = getCurrentDateMachineFormat();

  const [values, setValues] = useState({
    // INSURED INFORMATION
    a1_insured_first_name: '', // required field
    a2_insured_middle_name: '',
    a3_insured_last_name: '', // required field
    a4_insured_date_of_birth: '', // required field
    a5_insured_policy_number: '',
    // INSURED / POA CONTACT INFORMATION
    b1_best_person_to_contact: '',
    b2_power_of_attorney_first_name: '', // conditionally required
    b3_power_of_attorney_last_name: '',
    b4_insured_or_power_of_attorney_phone: '',
    b6_insured_or_power_of_attorney_email_address: '',
    // *** b7 has been removed ***
    b8_insured_or_power_of_attorney_preferred_additional_information: '',
    b9_insured_or_power_of_attorney_mailing_address_1: '', // required field
    b10_insured_or_power_of_attorney_mailing_address_2: '',
    b11_insured_or_power_of_attorney_mailing_city: '', // required field
    b12_insured_or_power_of_attorney_mailing_state: '', // required field
    b13_insured_or_power_of_attorney_mailing_zip: '', // required field
    // LONG TERM CARE INFORMATION
    c1_care_provider_info_first_name: '',
    c2_care_provider_info_last_name: '',
    // *** c3 has been removed ***
    c4_care_provider_info_phone: '',
    c5_care_provider_info_mailing_address: '',
    c6_care_provider_info_mailing_address_city: '',
    c7_care_provider_info_mailing_address_state: '',
    c8_care_provider_info_mailing_address_zip: '',
    c9_reason_applying_for_benefits: '',
    c10_assistance_with_activities_of_daily_living: '',
    c11_assistance_with_activities_of_daily_living_if_yes_describe: '',
    c12_substantial_supervision_cognitive_impairment: '',
    c13_substantial_supervision_cognitive_impairment_if_yes_describe: '',
    c14_date_when_need_for_assistance_and_or_supervision_started: '',
    // LONG TERM CARE INFORMATION - CONT'D (FORMAL SERVICES)
    d1_have_any_formal_care_services_been_provided: '',
    d2_ltc_service_provider_first_name: '',
    d3_ltc_service_provider_last_name: '',
    d4_ltc_service_provider_organization_name: '',
    d5_ltc_service_provider_phone: '',
    d6_ltc_service_provider_mailing_address: '',
    d7_ltc_service_provider_mailing_address_city: '',
    d8_ltc_service_provider_mailing_address_state: '',
    d9_ltc_service_provider_mailing_address_zip: '',
    // OTHER INSURANCE
    e1_insured_has_other_ltc_insurance: '',
    e2_insured_has_nm_group_di_insurance: '',
    // LONG TERM CARE NOTIFICATION COMPLETED BY
    f1_ltc_notification_completed_by: '', // required field
    g1_insured_rep_name: '',
    g2_insured_rep_phone: '',
    g3_insured_rep_relationship: '',
    h1_fr_or_afr_name: '',
    h2_fr_or_afr_number: '',
    h3_fr_or_afr_phone: '',
    h4_fr_or_afr_notifed_date: '', // conditionally required field
  });

  // if any fields are commented out, that means they do not currently have error states
  const [errors, setErrors] = useState({
    a1_insured_first_name: false, // required field
    // a2_insured_middle_name: false,
    a3_insured_last_name: false, // required field
    a4_insured_date_of_birth: false, // required field
    // a5_insured_policy_number: false,
    // b1_best_person_to_contact: false,
    b2_power_of_attorney_first_name: false, // conditionally required
    b3_power_of_attorney_last_name: false, // conditionally required
    b4_insured_or_power_of_attorney_phone: false,
    b6_insured_or_power_of_attorney_email_address: false,
    // *** b7 has been removed ***
    // b8_insured_or_power_of_attorney_preferred_additional_information: false,
    b9_insured_or_power_of_attorney_mailing_address_1: false, // required field
    // b10_insured_or_power_of_attorney_mailing_address_2: false,
    b11_insured_or_power_of_attorney_mailing_city: false, // required field
    b12_insured_or_power_of_attorney_mailing_state: false, // required field
    b13_insured_or_power_of_attorney_mailing_zip: false, // required field
    c1_care_provider_info_first_name: false,
    c2_care_provider_info_last_name: false,
    // *** c3 has been removed ***
    c4_care_provider_info_phone: false,
    // c5_care_provider_info_mailing_address: false,
    // c6_care_provider_info_mailing_address_city: false,
    // c7_care_provider_info_mailing_address_state: false,
    c8_care_provider_info_mailing_address_zip: false,
    // c9_reason_applying_for_benefits: false,
    // c10_assistance_with_activities_of_daily_living: false,
    // c11_assistance_with_activities_of_daily_living_if_yes_describe: false,
    // c12_substantial_supervision_cognitive_impairment: false,
    // c13_substantial_supervision_cognitive_impairment_if_yes_describe: false,
    c14_date_when_need_for_assistance_and_or_supervision_started: false,
    // d1_have_any_formal_care_services_been_provided: false,
    d2_ltc_service_provider_first_name: false,
    d3_ltc_service_provider_last_name: false,
    // d4_ltc_service_provider_organization_name: false,
    d5_ltc_service_provider_phone: false,
    // d6_ltc_service_provider_mailing_address: false,
    // d7_ltc_service_provider_mailing_address_city: false,
    // d8_ltc_service_provider_mailing_address_state: false,
    d9_ltc_service_provider_mailing_address_zip: false,
    // e1_insured_has_other_ltc_insurance: false,
    // e2_insured_has_nm_group_di_insurance: false,
    f1_ltc_notification_completed_by: false, // required field
    g1_insured_rep_name: false,
    g2_insured_rep_phone: false,
    // g3_insured_rep_relationship: false,
    h1_fr_or_afr_name: false,
    // h2_fr_or_afr_number: false,
    h3_fr_or_afr_phone: false,
    h4_fr_or_afr_notifed_date: false, // CONDITIONALLY required field
  });

  const { config } = useSiteData();
  const theme = useContext(ThemeContext);
  const largeUp = theme.largeUp();
  // other states
  const [formIsReadyForValidation, setFormIsReadyForValidation] = useState(false);
  const [isLargeUp, setIsLargeUp] = useState(largeUp);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  let headerEl;
  let headerElHeight;
  let mobileHeaderEl;
  let mobileHeaderElHeight;

  //  ░█████╗░██╗░░██╗░█████╗░███╗░░██╗░██████╗░███████╗// ██╗░░██╗░█████╗░███╗░░██╗██████╗░██╗░░░░░███████╗██████╗░░██████╗
  //  ██╔══██╗██║░░██║██╔══██╗████╗░██║██╔════╝░██╔════╝// ██║░░██║██╔══██╗████╗░██║██╔══██╗██║░░░░░██╔════╝██╔══██╗██╔════╝
  //  ██║░░╚═╝███████║███████║██╔██╗██║██║░░██╗░█████╗░░// ███████║███████║██╔██╗██║██║░░██║██║░░░░░█████╗░░██████╔╝╚█████╗░
  //  ██║░░██╗██╔══██║██╔══██║██║╚████║██║░░╚██╗██╔══╝░░// ██╔══██║██╔══██║██║╚████║██║░░██║██║░░░░░██╔══╝░░██╔══██╗░╚═══██╗
  //  ╚█████╔╝██║░░██║██║░░██║██║░╚███║╚██████╔╝███████╗// ██║░░██║██║░░██║██║░╚███║██████╔╝███████╗███████╗██║░░██║██████╔╝
  //  ░╚════╝░╚═╝░░╚═╝╚═╝░░╚═╝╚═╝░░╚══╝░╚═════╝░╚══════╝// ╚═╝░░╚═╝╚═╝░░╚═╝╚═╝░░╚══╝╚═════╝░╚══════╝╚══════╝╚═╝░░╚═╝╚═════╝░

  const analyticsPush = (e) => {
    const { name } = e.target;

    // adobe datalayer push
    window.adobeDataLayer.push({
      event: 'form-interact',
      form: {
        name : FORM_NAME,
        experience: FORM_NAME,
        'form-type': 'simple',
        'action-keys': name,
        'action-values': 'completed',
      },
    });
  };

  const handleChange = (e, jumpLinkId) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });

    // gathering current nav height
    if(jumpLinkId){
      headerEl = document.querySelector('#nmx-header > .nmx-container');
      headerElHeight = headerEl.clientHeight;
      mobileHeaderEl = document.querySelector('.nmx-header-logo-container');
      mobileHeaderElHeight = mobileHeaderEl.clientHeight;
      // allow new, conditionally rendered, content to load, and then scroll to appropriate position to bring new content into view
      setTimeout(() => {
        // get target element
        const el = document.getElementById(jumpLinkId);
        // setting offset for desktop
        let elPosY;
        if(isLargeUp){
          elPosY = window.pageYOffset + el.getBoundingClientRect().top - headerElHeight;
        }else {
          elPosY = window.pageYOffset + el.getBoundingClientRect().top - mobileHeaderElHeight;
        }
        // scroll to desired location
        window.scroll({
          top: elPosY,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  const handleDateChange = (e) => {
    // convert date picker from yyyy-mm-dd to mm/dd/yyyy format
    let formattedDate;
    const date = new Date(`${e.target.value}T00:00`);
    if (!Number.isNaN(date.getTime())) {
      formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    }

    setValues({
      ...values,
      [e.target.name]: formattedDate || '',
    });
  };

  const handlePhoneKeyUp = (e) => {
    // format the phone number on keyUp, i.e. 5555555555 autoformats to (555) 555-5555
    const phoneInput = e.target.value;

    // If the formatted num ends up being valid
    if (isPhoneNumber(phoneInput)) {
      const formattedPhoneInput = autoFormatPhone(phoneInput);
      // set the new value
      setValues({
        ...values,
        [e.target.name]: formattedPhoneInput,
      });
    }
  };

  const adobeErrorPush = () => {
    window.adobeDataLayer.push({
      event: 'form-error',
      form:{
        name : FORM_NAME,
        experience: 'other',
        'form-type': 'simple',
        'submission-type': 'other',
        'submission-error': true,
      },
    });
  };

  // ░██████╗██╗░░░██╗██████╗░███╗░░░███╗██╗████████╗
  // ██╔════╝██║░░░██║██╔══██╗████╗░████║██║╚══██╔══╝
  // ╚█████╗░██║░░░██║██████╦╝██╔████╔██║██║░░░██║░░░
  // ░╚═══██╗██║░░░██║██╔══██╗██║╚██╔╝██║██║░░░██║░░░
  // ██████╔╝╚██████╔╝██████╦╝██║░╚═╝░██║██║░░░██║░░░
  // ╚═════╝░░╚═════╝░╚═════╝░╚═╝░░░░░╚═╝╚═╝░░░╚═╝░░░

  // order of execution
  // 1. Submit button pressed
  // 2. trimValues() fires
  // 3. setFormIsReadyForValidation updates
  // 4. validateForm() fires
  // 5. useEffect fires upon error state and isSubmitting state updating
  // 6. errorCheck() fires to check for errors in the error state object
  // 7a. If the form is free of errors and isSubmitting:
  //    - execute executeRecaptchaAndSubmit()
  //      - execute handleReCaptchaCallback()
  // 7b. If the form has errors:
  //    - execute resetRecaptcha()

  // fires on submit
  const validateForm = () => {

    setIsSubmitting(true);

    // Validates the form on submit and sets error states
    // true is bad
    setErrors({
      a1_insured_first_name: validateNameField(values.a1_insured_first_name, NAME_FIELD_MAX_LENGTH, REQUIRED), // required field
      a3_insured_last_name: validateNameField(values.a3_insured_last_name, LAST_NAME_FIELD_MAX_LENGTH, REQUIRED), // required field
      a4_insured_date_of_birth: validateDateField(values.a4_insured_date_of_birth, DATE_MUST_BE_PRIOR_DATE, REQUIRED), // required field
      b2_power_of_attorney_first_name: validateNameField(values.b2_power_of_attorney_first_name, NAME_FIELD_MAX_LENGTH, (values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE)), // conditionally required
      b3_power_of_attorney_last_name: validateNameField(values. b3_power_of_attorney_last_name, LAST_NAME_FIELD_MAX_LENGTH, (values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE)), // conditionally required
      b4_insured_or_power_of_attorney_phone: validatePhoneNumberField(values.b4_insured_or_power_of_attorney_phone),
      b6_insured_or_power_of_attorney_email_address: validateEmailField(values.b6_insured_or_power_of_attorney_email_address),
      b9_insured_or_power_of_attorney_mailing_address_1: validateTextField(values.b9_insured_or_power_of_attorney_mailing_address_1, MAILING_ADDRESS_MAX_LENGTH, REQUIRED), // required
      b11_insured_or_power_of_attorney_mailing_city: validateTextField(values.b11_insured_or_power_of_attorney_mailing_city, CITY_NAME_MAX_LENGTH, REQUIRED), // required
      b12_insured_or_power_of_attorney_mailing_state: isEmpty(values.b12_insured_or_power_of_attorney_mailing_state), // required
      b13_insured_or_power_of_attorney_mailing_zip: validateZipField(values.b13_insured_or_power_of_attorney_mailing_zip, REQUIRED), // required
      c1_care_provider_info_first_name: validateNameField(values.c1_care_provider_info_first_name, NAME_FIELD_MAX_LENGTH),
      c2_care_provider_info_last_name: validateNameField(values.c2_care_provider_info_last_name, LAST_NAME_FIELD_MAX_LENGTH),
      c4_care_provider_info_phone: validatePhoneNumberField(values.c4_care_provider_info_phone),
      c8_care_provider_info_mailing_address_zip: validateZipField(values.c8_care_provider_info_mailing_address_zip),
      c14_date_when_need_for_assistance_and_or_supervision_started: validateDateField(values.c14_date_when_need_for_assistance_and_or_supervision_started),
      d2_ltc_service_provider_first_name: validateNameField(values.d2_ltc_service_provider_first_name, NAME_FIELD_MAX_LENGTH),
      d3_ltc_service_provider_last_name: validateNameField(values.d3_ltc_service_provider_last_name, LAST_NAME_FIELD_MAX_LENGTH),
      d5_ltc_service_provider_phone: validatePhoneNumberField(values.d5_ltc_service_provider_phone),
      d9_ltc_service_provider_mailing_address_zip: validateZipField(values.d9_ltc_service_provider_mailing_address_zip),
      f1_ltc_notification_completed_by: isEmpty(values.f1_ltc_notification_completed_by), // required
      g1_insured_rep_name: validateNameField(values.g1_insured_rep_name, FREE_FORM_NAME_FIELD),
      g2_insured_rep_phone: validatePhoneNumberField(values.g2_insured_rep_phone),
      h3_fr_or_afr_phone: validatePhoneNumberField(values.h3_fr_or_afr_phone),
      h1_fr_or_afr_name: validateNameField(values.h1_fr_or_afr_name, FREE_FORM_NAME_FIELD),
      // If Notification was Completed by FR, the Date Notified becomes required, and must be a valid date if entered.
      h4_fr_or_afr_notifed_date: validateDateField(values.h4_fr_or_afr_notifed_date, false, (values.f1_ltc_notification_completed_by === INSUREDS_FR)), // conditionally required
    });
    setFormIsReadyForValidation(false);
  };

  // fires on submit
  const trimValues = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const updatedValues = { ...values }; // Create a copy of the values object

    Object.keys(updatedValues).forEach((key) => {
      updatedValues[key] = updatedValues[key].replace(trimStringRegex, '').trim(); // remove leading and trailing spaces from all values
    });

    setValues(updatedValues); // Update the state with the updated values
    setFormIsReadyForValidation(true);
  };

  const handleReCaptchaCallback = (recaptchaReponseToken) => {
    setShowLoading(true);

    //   ██████╗░░█████╗░██╗░░░██╗██╗░░░░░░█████╗░░█████╗░██████╗░
    //   ██╔══██╗██╔══██╗╚██╗░██╔╝██║░░░░░██╔══██╗██╔══██╗██╔══██╗
    //   ██████╔╝███████║░╚████╔╝░██║░░░░░██║░░██║███████║██║░░██║
    //   ██╔═══╝░██╔══██║░░╚██╔╝░░██║░░░░░██║░░██║██╔══██║██║░░██║
    //   ██║░░░░░██║░░██║░░░██║░░░███████╗╚█████╔╝██║░░██║██████╔╝
    //   ╚═╝░░░░░╚═╝░░╚═╝░░░╚═╝░░░╚══════╝░╚════╝░╚═╝░░╚═╝╚═════╝░

    try {
      const ajaxForm = new AjaxForm(); // Make sure this is correct
      ajaxForm.addField('a1_insured_first_name', values.a1_insured_first_name);
      ajaxForm.addField('a2_insured_middle_name', values.a2_insured_middle_name);
      ajaxForm.addField('a3_insured_last_name', values.a3_insured_last_name);
      ajaxForm.addField('a4_insured_date_of_birth', values.a4_insured_date_of_birth);
      ajaxForm.addField('a5_insured_policy_number', values.a5_insured_policy_number);
      ajaxForm.addField('b1_best_person_to_contact', values.b1_best_person_to_contact);
      ajaxForm.addField('b2_power_of_attorney_first_name', values.b2_power_of_attorney_first_name);
      ajaxForm.addField('b3_power_of_attorney_last_name', values.b3_power_of_attorney_last_name);
      ajaxForm.addField('b4_insured_or_power_of_attorney_phone', values.b4_insured_or_power_of_attorney_phone);
      ajaxForm.addField('b6_insured_or_power_of_attorney_email_address', values.b6_insured_or_power_of_attorney_email_address);
      ajaxForm.addField('b8_insured_or_power_of_attorney_preferred_additional_information', values.b8_insured_or_power_of_attorney_preferred_additional_information);
      ajaxForm.addField('b9_insured_or_power_of_attorney_mailing_address_1', values.b9_insured_or_power_of_attorney_mailing_address_1);
      ajaxForm.addField('b10_insured_or_power_of_attorney_mailing_address_2', values.b10_insured_or_power_of_attorney_mailing_address_2);
      ajaxForm.addField('b11_insured_or_power_of_attorney_mailing_city', values.b11_insured_or_power_of_attorney_mailing_city);
      ajaxForm.addField('b12_insured_or_power_of_attorney_mailing_state', values.b12_insured_or_power_of_attorney_mailing_state);
      ajaxForm.addField('b13_insured_or_power_of_attorney_mailing_zip', values.b13_insured_or_power_of_attorney_mailing_zip);
      ajaxForm.addField('c1_care_provider_info_first_name', values.c1_care_provider_info_first_name);
      ajaxForm.addField('c2_care_provider_info_last_name', values.c2_care_provider_info_last_name);
      ajaxForm.addField('c4_care_provider_info_phone', values.c4_care_provider_info_phone);
      ajaxForm.addField('c5_care_provider_info_mailing_address', values.c5_care_provider_info_mailing_address);
      ajaxForm.addField('c6_care_provider_info_mailing_address_city', values.c6_care_provider_info_mailing_address_city);
      ajaxForm.addField('c7_care_provider_info_mailing_address_state', values.c7_care_provider_info_mailing_address_state);
      ajaxForm.addField('c8_care_provider_info_mailing_address_zip', values.c8_care_provider_info_mailing_address_zip);
      ajaxForm.addField('c9_reason_applying_for_benefits', values.c9_reason_applying_for_benefits);
      ajaxForm.addField('c10_assistance_with_activities_of_daily_living', values.c10_assistance_with_activities_of_daily_living);
      ajaxForm.addField('c11_assistance_with_activities_of_daily_living_if_yes_describe', values.c11_assistance_with_activities_of_daily_living_if_yes_describe);
      ajaxForm.addField('c12_substantial_supervision_cognitive_impairment', values.c12_substantial_supervision_cognitive_impairment);
      ajaxForm.addField('c13_substantial_supervision_cognitive_impairment_if_yes_describe', values.c13_substantial_supervision_cognitive_impairment_if_yes_describe);
      ajaxForm.addField('c14_date_when_need_for_assistance_and_or_supervision_started', values.c14_date_when_need_for_assistance_and_or_supervision_started);
      ajaxForm.addField('d1_have_any_formal_care_services_been_provided', values.d1_have_any_formal_care_services_been_provided);
      ajaxForm.addField('d2_ltc_service_provider_first_name', values.d2_ltc_service_provider_first_name);
      ajaxForm.addField('d3_ltc_service_provider_last_name', values.d3_ltc_service_provider_last_name);
      ajaxForm.addField('d4_ltc_service_provider_organization_name', values.d4_ltc_service_provider_organization_name);
      ajaxForm.addField('d5_ltc_service_provider_phone', values.d5_ltc_service_provider_phone);
      ajaxForm.addField('d6_ltc_service_provider_mailing_address', values.d6_ltc_service_provider_mailing_address);
      ajaxForm.addField('d7_ltc_service_provider_mailing_address_city', values.d7_ltc_service_provider_mailing_address_city);
      ajaxForm.addField('d8_ltc_service_provider_mailing_address_state', values.d8_ltc_service_provider_mailing_address_state);
      ajaxForm.addField('d9_ltc_service_provider_mailing_address_zip', values.d9_ltc_service_provider_mailing_address_zip);
      ajaxForm.addField('e1_insured_has_other_ltc_insurance', values.e1_insured_has_other_ltc_insurance);
      ajaxForm.addField('e2_insured_has_nm_group_di_insurance', values.e2_insured_has_nm_group_di_insurance);
      ajaxForm.addField('f1_ltc_notification_completed_by', values.f1_ltc_notification_completed_by);
      ajaxForm.addField('g1_insured_rep_name', values.g1_insured_rep_name);
      ajaxForm.addField('g2_insured_rep_phone', values.g2_insured_rep_phone);
      ajaxForm.addField('g3_insured_rep_relationship', values.g3_insured_rep_relationship);
      ajaxForm.addField('h1_fr_or_afr_name', values.h1_fr_or_afr_name);
      ajaxForm.addField('h2_fr_or_afr_number', values.h2_fr_or_afr_number);
      ajaxForm.addField('h3_fr_or_afr_phone', values.h3_fr_or_afr_phone);
      ajaxForm.addField('h4_fr_or_afr_notifed_date', values.h4_fr_or_afr_notifed_date);
      ajaxForm.addField('poaNameRequired', (values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE)); // should equal true if contact is power of attorney
      ajaxForm.addField('frDateNotifiedRequired', (values.f1_ltc_notification_completed_by === INSUREDS_FR)); // should equal true if notification completed by FR
      ajaxForm.addField('recaptchaResponseToken', recaptchaReponseToken);
      ajaxForm
        .promiseSubmit('POST', config.public.noticeOfLongTermCareSubmissionUrl)
        .then(() => {
          // adobe datalayer push
          window.adobeDataLayer.push({
            event: 'form-complete',
            form:{
              name : FORM_NAME,
              experience: 'other',
              'form-type': 'simple',
              'submission-type': 'other',
            },
          });
          setShowSuccess(true);
          setShowLoading(false);
        }).catch((error) => {
          setShowSuccess(false);
          setShowLoading(false);
          setShowError(true);
          adobeErrorPush();
          console.error(`Error submitting prelead on ${FORM_NAME}`, error);
        }).then(() => {
          //
        });
    } catch (error) {
      setShowSuccess(false);
      setShowLoading(false);
      setShowError(true);
      adobeErrorPush();
      console.error(`Error submitting prelead on ${FORM_NAME}.`, error);
    }
  };

  const executeRecaptchaAndSubmit = () => {
    if (window.grecaptcha) {
      try {
        // recaptchaV3 method for executing
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(config.public.recaptchaV3, { action: 'submit' }).then((token) => {
            document
              .querySelectorAll('.recaptchaResponse')
              // eslint-disable-next-line no-param-reassign
              .forEach((elem) => { elem.value = token; });
            handleReCaptchaCallback(token);
          });
        });
      } catch (error) {
        // if recaptcha execute fails
        setShowSuccess(false);
        setShowError(true);
        adobeErrorPush();
        setShowLoading(false);
        console.error(`Error executing recaptcha at form submit at path: ${window.location.pathname}.`, error);
      }
    }
  };

  const resetRecaptcha = () => {
    setShowLoading(false);
    setShowSuccess(false);
    setIsSubmitting(false);
    // validation errors on the form, need to reset recaptcha
    try {
      window.grecaptcha.reset();
      //
    } catch (error) {
      //
    }
  };

  // ██╗░░██╗░█████╗░░█████╗░██╗░░██╗░██████╗
  // ██║░░██║██╔══██╗██╔══██╗██║░██╔╝██╔════╝
  // ███████║██║░░██║██║░░██║█████═╝░╚█████╗░
  // ██╔══██║██║░░██║██║░░██║██╔═██╗░░╚═══██╗
  // ██║░░██║╚█████╔╝╚█████╔╝██║░╚██╗██████╔╝
  // ╚═╝░░╚═╝░╚════╝░░╚════╝░╚═╝░░╚═╝╚═════╝░

  // useEffect fires when error states or the isSubmitting state updates.
  // Since the error states are initially set on page load, and are all false, we need to also check whether or not
  // the user is attempting to submit, otherwise submission would occur on page load.
  useEffect(() => {
    // checking form for errors
    const formHasErrors = errorCheck(errors);
    // if the user is attempting to submit the form and it is free of errors, submit the form
    if(isSubmitting){
      if(!formHasErrors){
        executeRecaptchaAndSubmit();
      }else{
        resetRecaptcha();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isSubmitting]);

  useEffect(() => {
    if (largeUp !== isLargeUp) {
      setIsLargeUp(largeUp);
    }
  }, [largeUp, isLargeUp]);

  useEffect(() => {
    // prevents execution on page load, and also prevents hook from firing when formIsReadyForValidation gets set back to false
    if(isInitialRender || !formIsReadyForValidation){
      setIsInitialRender(false);
      return;
    }

    validateForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formIsReadyForValidation]);

  // ░█████╗░░█████╗░███╗░░░███╗██████╗░░█████╗░███╗░░██╗███████╗███╗░░██╗████████╗
  // ██╔══██╗██╔══██╗████╗░████║██╔══██╗██╔══██╗████╗░██║██╔════╝████╗░██║╚══██╔══╝
  // ██║░░╚═╝██║░░██║██╔████╔██║██████╔╝██║░░██║██╔██╗██║█████╗░░██╔██╗██║░░░██║░░░
  // ██║░░██╗██║░░██║██║╚██╔╝██║██╔═══╝░██║░░██║██║╚████║██╔══╝░░██║╚████║░░░██║░░░
  // ╚█████╔╝╚█████╔╝██║░╚═╝░██║██║░░░░░╚█████╔╝██║░╚███║███████╗██║░╚███║░░░██║░░░
  // ░╚════╝░░╚════╝░╚═╝░░░░░╚═╝╚═╝░░░░░░╚════╝░╚═╝░░╚══╝╚══════╝╚═╝░░╚══╝░░░╚═╝░░░

  return (
    <SectionContainer id='notice-of-long-term-care-form'>
      {(!showSuccess && !showError && !showLoading) && (
        <>
          <Row align='center'>
            <Col
              large={10}
              xlarge={8}>
              <Typography
                component='h1'
                nextElement='typography'
                align='center'>
                Notice of Long Term <WidowBlock>Care Claim</WidowBlock>
              </Typography>
              <Typography nextElement='typography'>
                Use this notice to inform Northwestern Mutual Long Term Care Company of your request for long term care benefits. Upon receipt we will prepare a personalized claim kit and send it to the Insured or the Insured&#39;s claim representative if applicable.
              </Typography>
              <Typography
                nextElement='typography'
                weight={500}>
                Note: To be eligible for long&#45;term care benefits, you must have a 90 consecutive day or longer need for long&#45;term care services requiring either hands&#45;on or stand&#45;by assistance with at least 2 of 6 Activities of Daily Living and&#47;or Substantial Supervision to protect your health and safety due to severe cognitive impairment.
              </Typography>
              <Typography
                nextElement='typography'
                disableBottomPadding>
                The 6 Activities of Daily Living include:
              </Typography>
              <BulletedListStyled
                bulletType='filledCircle'
                bulletedListData={[
                  { bulletText: 'Eating' },
                  { bulletText: 'Toileting' },
                  { bulletText: 'Transferring' },
                  { bulletText: 'Bathing' },
                  { bulletText: 'Dressing' },
                  { bulletText: 'Continence' },
                ]}
              />
              <Typography nextElement='typography'>
                If you have any questions, call Client Services at{' '}
                <Link
                  variant='primaryDeemphasize'
                  id='client-services-phone-link'
                  linkUrl='tel:8007489493'>
                  &#40;800&#41; 748&#45;9493
                </Link>
                , <ClientServicesHours7to5 />.
              </Typography>
              <Typography nextElement='graphic'>
                All fields marked with &#42; are required.
              </Typography>
            </Col>
          </Row>
          {/* START FORM ////////////////////////////////////////////// */}
          <Row align='center'>
            <Col large={8}>
              <FormStyled
                legend={FORM_NAME}
                onSubmit={trimValues}>
                <fieldset>  {/* INSURED INFORMATION //////////////////////////////////////////////////////////////////////// */}
                  <LegendStyled>
                    Insured Information
                  </LegendStyled>
                  <Row>
                    <Col>
                      <Typography
                        component='h2'
                        nextElement='typography'>
                        Insured Information
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <ColStyled medium={4}>
                      <TextField
                        id='a1_insured_first_name'
                        name='a1_insured_first_name'
                        labelText='First Name'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.a1_insured_first_name}
                        hasError={errors.a1_insured_first_name}
                        helperText={setErrorMessage(errors.a1_insured_first_name, 'Please enter a valid first name.')}
                        maxLength={NAME_FIELD_MAX_LENGTH}
                        required
                      />
                    </ColStyled>
                    <ColStyled medium={4}>
                      <TextField
                        id='a2_insured_middle_name'
                        name='a2_insured_middle_name'
                        labelText='Middle Name'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.a2_insured_middle_name}
                        maxLength={NAME_FIELD_MAX_LENGTH}
                      />
                    </ColStyled>
                    <ColStyled medium={4}>
                      <TextField
                        id='a3_insured_last_name'
                        name='a3_insured_last_name'
                        labelText='Last Name'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.a3_insured_last_name}
                        hasError={errors.a3_insured_last_name}
                        helperText={setErrorMessage(errors.a3_insured_last_name, 'Please enter a valid last name.')}
                        maxLength={LAST_NAME_FIELD_MAX_LENGTH}
                        required
                      />
                    </ColStyled>
                  </Row>
                  <Row>
                    <ColStyled medium={6}>
                      <TextField
                        id='a4_insured_date_of_birth'
                        name='a4_insured_date_of_birth'
                        type='date'
                        max={currentDate}
                        maxLength={DATE_MAX_LENGTH}
                        labelText='Date of Birth'
                        onBlur={analyticsPush}
                        onChange={handleDateChange}
                        hasError={errors.a4_insured_date_of_birth}
                        helperText={setErrorMessage(errors.a4_insured_date_of_birth, 'Please enter a valid date.')}
                        required
                      />
                    </ColStyled>
                    <ColStyled
                      medium={6}
                      lastItem>
                      <TextField
                        id='a5_insured_policy_number'
                        name='a5_insured_policy_number'
                        labelText='Policy number'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.a5_insured_policy_number}
                        maxLength={10}
                        placeholderText='Enter policy number if known'
                      />
                    </ColStyled>
                  </Row>
                  <fieldset> {/* START REASON FOR CLAIM */}
                    <LegendStyled>
                      Reason for Claim
                    </LegendStyled>
                    <Row>
                      <Col medium={6}>
                        <Typography
                          component='h3'
                          nextElement='typography'>
                          Reason for Claim
                        </Typography>
                      </Col>
                    </Row>
                    <Row>
                      <ColStyled medium={6}>
                        <TextField
                          id='c9_reason_applying_for_benefits'
                          name='c9_reason_applying_for_benefits'
                          labelText={<>Diagnosis&#47;Reason Applying for Benefits</>}
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.c9_reason_applying_for_benefits}
                          maxLength={150}
                        />
                      </ColStyled>
                    </Row>
                    {/* START ASSISTANCE WITH ACTIVITIES RADIO GROUP */}
                    <fieldset id='assistance-with-daily-living-radio'>
                      <LegendStyled>
                        Indicate if assistance with Activities of Daily Living &#40;ADL&#41; is required
                      </LegendStyled>
                      <Row>
                        <ColStyled>
                          <RadioGroup
                            id='c10_assistance_with_activities_of_daily_living'
                            name='c10_assistance_with_activities_of_daily_living'
                            labelText='Indicate if assistance with Activities of Daily Living (ADL) is required:'
                            onBlur={analyticsPush}
                            onChange={(e) => handleChange(e, 'assistance-with-daily-living-radio')}
                            options={[
                              {
                                label: YES,
                                value: YES,
                              },
                              {
                                label: NO,
                                value: NO,
                              },
                            ]}
                          />
                        </ColStyled>
                      </Row>
                    </fieldset>
                    {/* END ASSISTANCE WITH ACTIVITIES RADIO GROUP */}
                    {values.c10_assistance_with_activities_of_daily_living === YES && (
                      <Row>
                        <ColStyled
                          medium={8}
                          lastItem>
                          <LabelStyled htmlFor='c11_assistance_with_activities_of_daily_living_if_yes_describe'>
                            If yes, please describe:
                          </LabelStyled>
                          <TextAreaStyled
                            id='c11_assistance_with_activities_of_daily_living_if_yes_describe'
                            name='c11_assistance_with_activities_of_daily_living_if_yes_describe'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.c11_assistance_with_activities_of_daily_living_if_yes_describe}
                            maxLength={350}
                          />
                        </ColStyled>
                      </Row>
                    )}
                    <fieldset id='assistance-with-cognitive-impairment-radio'> {/* START SUBSTANTIAL SUPERVISION RADIO GROUP */}
                      <LegendStyled>
                        Indicate if substantial supervision for a Cognitive Impairment is required:
                      </LegendStyled>
                      <Row>
                        <ColStyled>
                          <RadioGroup
                            id='c12_substantial_supervision_cognitive_impairment'
                            name='c12_substantial_supervision_cognitive_impairment'
                            labelText='Indicate if substantial supervision for a Cognitive Impairment is required:'
                            onBlur={analyticsPush}
                            onChange={(e) => handleChange(e, 'assistance-with-cognitive-impairment-radio')}
                            options={[
                              {
                                label: YES,
                                value: YES,
                              },
                              {
                                label: NO,
                                value: NO,
                              },
                            ]}
                          />
                        </ColStyled>
                      </Row>
                    </fieldset> {/* END SUBSTANTIAL SUPERVISION RADIO GROUP */}
                    {values.c12_substantial_supervision_cognitive_impairment === YES && (
                      <Row>
                        <ColStyled
                          medium={8}
                          lastItem>
                          <LabelStyled htmlFor='c13_substantial_supervision_cognitive_impairment_if_yes_describe'>
                            If yes, please describe:
                          </LabelStyled>
                          <TextAreaStyled
                            id='c13_substantial_supervision_cognitive_impairment_if_yes_describe'
                            name='c13_substantial_supervision_cognitive_impairment_if_yes_describe'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.c13_substantial_supervision_cognitive_impairment_if_yes_describe}
                            maxLength={350}
                          />
                        </ColStyled>
                      </Row>
                    )}
                    <Row>
                      <ColStyled medium={6}>
                        <TextField
                          id='c14_date_when_need_for_assistance_and_or_supervision_started'
                          name='c14_date_when_need_for_assistance_and_or_supervision_started'
                          type='date'
                          maxLength={DATE_MAX_LENGTH}
                          labelText={<>Date when need for assistance and&#47;or supervision started:</>}
                          onBlur={analyticsPush}
                          onChange={handleDateChange}
                          hasError={errors.c14_date_when_need_for_assistance_and_or_supervision_started}
                          helperText={setErrorMessage(errors.c14_date_when_need_for_assistance_and_or_supervision_started, 'Please enter a valid date.')}
                        />
                      </ColStyled>
                    </Row>
                  </fieldset> {/* END REASON FOR CLAIM */}
                </fieldset>  {/* END INSURED INFORMATION */}
                {/* ////////////////////////////////////////////////////////////////////////////// */}
                <fieldset>  {/* START INSURED / POA CONTACT INFORMATION */}
                  <LegendStyled>
                    Claim Contact
                  </LegendStyled>
                  <Row>
                    <Col>
                      <Typography
                        component='h2'
                        nextElement='typography'>
                        Claim Contact
                      </Typography>
                    </Col>
                  </Row>
                  <fieldset> {/* START RADIO GROUP */}
                    <LegendStyled>
                      Best Person to Contact
                    </LegendStyled>
                    <Row>
                      <ColStyled>
                        <RadioGroup
                          id='b1_best_person_to_contact'
                          name='b1_best_person_to_contact'
                          labelText='Indicate who is the best person to contact to schedule a telephone interview to obtain claim details:'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          options={[
                            {
                              label: INSURED,
                              value: INSURED,
                            },
                            {
                              label: INSUREDS_REPRESENTATIVE,
                              value: INSUREDS_REPRESENTATIVE,
                              // selected: true,
                            },
                          ]}
                        />
                      </ColStyled>
                    </Row>
                  </fieldset>
                  {/* END RADIO GROUP */}
                  {/* Conditionally render if user selects Insured's Power of Attorney from the radio group */}
                  {values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE  && (
                    <>
                      {/* START REPRESENTATIVE ROLE RADIO GROUP */}
                      <fieldset id='representative-role-selection-radio'>
                        <LegendStyled>
                          Indicate your role as the insured&#39;s representative
                        </LegendStyled>
                        <Row>
                          <ColStyled>
                            {/* temporary has no functionality and does not get sent in the payload on submit. It's a dummy form input that was requested by the claims team. ¯\_(ツ)_/¯ */}
                            <RadioGroup
                              id='b14_insured_or_power_of_attorney_role'
                              name='b14_insured_or_power_of_attorney_role'
                              labelText='Please select a role:'
                              onBlur={analyticsPush}
                              options={[
                                {
                                  label: 'Attorney-in-fact',
                                  value: 'Attorney-in-fact',
                                },
                                {
                                  label: 'Family Member',
                                  value: 'Family Member',
                                },
                                {
                                  label: 'Legal Guardian',
                                  value: 'Legal Guardian',
                                },
                                {
                                  label: 'Other',
                                  value: 'Other',
                                },
                                {
                                  label: 'Spouse',
                                  value: 'Spouse',
                                },
                              ]}
                            />
                          </ColStyled>
                        </Row>
                      </fieldset>
                      {/* END REPRESENTATIVE ROLE RADIO GROUP */}
                      <Row>
                        <Col medium={10}>
                          <Typography
                            component='h3'
                            nextElement='typography'>
                            Name of {INSUREDS_REPRESENTATIVE}
                          </Typography>
                        </Col>
                      </Row>
                      <Row>
                        <ColStyled medium={6}>
                          <TextField
                            id='b2_power_of_attorney_first_name'
                            name='b2_power_of_attorney_first_name'
                            labelText='First Name'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.b2_power_of_attorney_first_name}
                            hasError={errors.b2_power_of_attorney_first_name}
                            helperText={setErrorMessage(errors.b2_power_of_attorney_first_name, 'Please enter a valid first name.')}
                            required={values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE}
                            maxLength={NAME_FIELD_MAX_LENGTH}
                          />
                        </ColStyled>
                        <ColStyled medium={6}>
                          <TextField
                            id='b3_power_of_attorney_last_name'
                            name='b3_power_of_attorney_last_name'
                            labelText='Last Name'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.b3_power_of_attorney_last_name}
                            hasError={errors.b3_power_of_attorney_last_name}
                            helperText={setErrorMessage(errors.b3_power_of_attorney_last_name, 'Please enter a valid last name.')}
                            required={values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE}
                            maxLength={LAST_NAME_FIELD_MAX_LENGTH}
                          />
                        </ColStyled>
                      </Row>
                    </>
                  )}
                  <Row>
                    <ColStyled medium={6}>
                      <TextField
                        id='b4_insured_or_power_of_attorney_phone'
                        name='b4_insured_or_power_of_attorney_phone'
                        type='tel'
                        labelText={
                          values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE
                            ? <>Insured&#39;s representative&#39;s phone number</>
                            : <>Insured&#39;s phone number</>
                        }
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        onKeyUp={handlePhoneKeyUp}
                        value={values.b4_insured_or_power_of_attorney_phone}
                        hasError={errors.b4_insured_or_power_of_attorney_phone}
                        helperText={setErrorMessage(errors.b4_insured_or_power_of_attorney_phone, 'Please enter a valid phone number.')}
                        maxLength={PHONE_MAX_LENGTH}
                      />
                    </ColStyled>
                  </Row>
                  <Row>
                    <ColStyled medium={6}>
                      <TextField
                        id='b6_insured_or_power_of_attorney_email_address'
                        name='b6_insured_or_power_of_attorney_email_address'
                        type='email'
                        labelText={
                          values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE
                            ? <>Insured&#39;s representative&#39;s email address</>
                            : <>Insured&#39;s email address</>
                        }
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.b6_insured_or_power_of_attorney_email_address}
                        hasError={errors.b6_insured_or_power_of_attorney_email_address}
                        helperText={setErrorMessage(errors.b6_insured_or_power_of_attorney_email_address, 'Please enter a valid email.')}
                        maxLength={EMAIL_MAX_LENGTH}
                      />
                    </ColStyled>
                  </Row>
                  <Row>
                    <ColStyled
                      medium={8}
                      lastItem>
                      <LabelStyled htmlFor='b8_insured_or_power_of_attorney_preferred_additional_information'>
                        Please provide any additional information
                      </LabelStyled>
                      <TextAreaStyled
                        id='b8_insured_or_power_of_attorney_preferred_additional_information'
                        name='b8_insured_or_power_of_attorney_preferred_additional_information'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.b8_insured_or_power_of_attorney_preferred_additional_information}
                        maxLength={255}
                      />
                    </ColStyled>
                  </Row>
                  <fieldset> {/* START INSURED OR POWER OF ATTORNEY MAILING ADDRESS */}
                    <LegendStyled>
                      Mailing Address for insured or insured&#39;s representative
                    </LegendStyled>
                    <Row>
                      <Col medium={6}>
                        <Typography
                          component='h3'
                          nextElement='typography'>
                          {
                            values.b1_best_person_to_contact === INSUREDS_REPRESENTATIVE
                              ? <>Mailing Address for insured&#39;s representative</>
                              : <>Mailing Address for insured</>
                          }
                        </Typography>
                      </Col>
                    </Row>
                    <Row>
                      <ColStyled medium={6}>
                        <TextField
                          id='b9_insured_or_power_of_attorney_mailing_address_1'
                          name='b9_insured_or_power_of_attorney_mailing_address_1'
                          labelText='Address 1'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.b9_insured_or_power_of_attorney_mailing_address_1}
                          hasError={errors.b9_insured_or_power_of_attorney_mailing_address_1}
                          helperText={setErrorMessage(errors.b9_insured_or_power_of_attorney_mailing_address_1, 'Please enter a mailing address.')}
                          maxLength={MAILING_ADDRESS_MAX_LENGTH}
                          required
                        />
                      </ColStyled>
                    </Row>
                    <Row>
                      <ColStyled medium={6}>
                        <TextField
                          id='b10_insured_or_power_of_attorney_mailing_address_2'
                          name='b10_insured_or_power_of_attorney_mailing_address_2'
                          labelText='Address 2'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.b10_insured_or_power_of_attorney_mailing_address_2}
                          maxLength={MAILING_ADDRESS_MAX_LENGTH}
                        />
                      </ColStyled>
                    </Row>
                    <Row>
                      <ColStyled medium={4}>
                        <TextField
                          id='b11_insured_or_power_of_attorney_mailing_city'
                          name='b11_insured_or_power_of_attorney_mailing_city'
                          labelText='City'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.b11_insured_or_power_of_attorney_mailing_city}
                          hasError={errors.b11_insured_or_power_of_attorney_mailing_city}
                          helperText={setErrorMessage(errors.b11_insured_or_power_of_attorney_mailing_city, 'Please enter a city name.')}
                          maxLength={CITY_NAME_MAX_LENGTH}
                          required
                        />
                      </ColStyled>
                      <ColStyled medium={4}>
                        <Select
                          id='b12_insured_or_power_of_attorney_mailing_state'
                          name='b12_insured_or_power_of_attorney_mailing_state'
                          labelText='State'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.b12_insured_or_power_of_attorney_mailing_state}
                          hasError={errors.b12_insured_or_power_of_attorney_mailing_state}
                          helperText={setErrorMessage(errors.b12_insured_or_power_of_attorney_mailing_state, 'Please select a state.')}
                          options={stateOptionsList}
                          maxLength={2}
                          required
                        />
                      </ColStyled>
                      <ColStyled
                        medium={4}
                        lastItem>
                        <TextField
                          id='b13_insured_or_power_of_attorney_mailing_zip'
                          name='b13_insured_or_power_of_attorney_mailing_zip'
                          labelText='ZIP Code'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.b13_insured_or_power_of_attorney_mailing_zip}
                          hasError={errors.b13_insured_or_power_of_attorney_mailing_zip}
                          helperText={setErrorMessage(errors.b13_insured_or_power_of_attorney_mailing_zip, 'Please enter a valid ZIP code.')}
                          maxLength={ZIP_MAX_LENGTH}
                          required
                        />
                      </ColStyled>
                    </Row>
                  </fieldset> {/* END INSURED OR POWER OF ATTORNEY MAILING ADDRESS */}
                  <Row>
                    <Col>
                      <Typography nextElement='typography'>
                        Note: The Power of Attorney document will be reviewed when it is received by NLTC. Please include the LTC policy number and insured name on the POA document copy and send:{' '}
                      </Typography>
                      <BulletedListStyled
                        bulletedListData={[
                          {
                            bulletText: <>Email:{' '}
                              <LinkStyled
                                id='notice-of-long-term-care-form-benefits-email-link'
                                variant='primaryDeemphasize'
                                linkUrl='mailto:longtermcarebenefits@northwesternmutual.com'
                                ariaLabel='email long term care benefits at northwestern mutual.'>
                                LongTermCareBenefits&#64;northwesternmutual.com
                              </LinkStyled>
                            </>,
                          },
                          { bulletText: <>Fax: 414&#45;625&#45;5821</> },
                          {
                            bulletText: <>Mail:{' '}
                              <AddressStyled>
                                Northwestern Long Term Care Claims PO Box 3450 Milwaukee WI 53201&#45;3230.
                              </AddressStyled>
                            </>,
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </fieldset> {/* END INSURED / POA CONTACT INFORMATION */}
                {/* //////////////////////////////////////////////////////////// */}
                <fieldset>  {/* START LONG TERM CARE INFORMATION */}
                  <Row>
                    <Col>
                      <Typography
                        component='h2'
                        nextElement='typography'>
                        Long Term <WidowBlock>Care Information</WidowBlock>
                      </Typography>
                      <legend>
                        <Typography
                          component='h3'
                          nextElement='typography'>
                          Primary Care <WidowBlock>Physician Information</WidowBlock>
                        </Typography>
                      </legend>
                    </Col>
                  </Row>
                  <Row>
                    <ColStyled medium={6}>
                      <TextField
                        id='c1_care_provider_info_first_name'
                        name='c1_care_provider_info_first_name'
                        labelText='First Name'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.c1_care_provider_info_first_name}
                        hasError={errors.c1_care_provider_info_first_name}
                        helperText={setErrorMessage(errors.c1_care_provider_info_first_name, 'Please enter a valid first name.')}
                        maxLength={NAME_FIELD_MAX_LENGTH}
                      />
                    </ColStyled>
                    <ColStyled medium={6}>
                      <TextField
                        id='c2_care_provider_info_last_name'
                        name='c2_care_provider_info_last_name'
                        labelText='Last Name'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        value={values.c2_care_provider_info_last_name}
                        hasError={errors.c2_care_provider_info_last_name}
                        helperText={setErrorMessage(errors.c2_care_provider_info_last_name, 'Please enter a valid last name.')}
                        maxLength={LAST_NAME_FIELD_MAX_LENGTH}
                      />
                    </ColStyled>
                    <ColStyled
                      medium={6}
                      lastItem>
                      <TextField
                        id='c4_care_provider_info_phone'
                        name='c4_care_provider_info_phone'
                        type='tel'
                        labelText='Phone Number'
                        onBlur={analyticsPush}
                        onChange={handleChange}
                        onKeyUp={handlePhoneKeyUp}
                        value={values.c4_care_provider_info_phone}
                        hasError={errors.c4_care_provider_info_phone}
                        helperText={setErrorMessage(errors.c4_care_provider_info_phone, 'Please enter a valid phone number.')}
                        maxLength={PHONE_MAX_LENGTH}
                      />
                    </ColStyled>
                  </Row>
                  <fieldset> {/* START PRIMARY CARE PROVIDER MAILING ADDRESS */}
                    <LegendStyled>
                      Primary Care Provider Mailing address
                    </LegendStyled>
                    <Row>
                      <Col medium={6}>
                        <Typography
                          component='h3'
                          nextElement='typography'>
                          Mailing Address
                        </Typography>
                      </Col>
                    </Row>
                    <Row>
                      <ColStyled medium={6}>
                        <TextField
                          id='c5_care_provider_info_mailing_address'
                          name='c5_care_provider_info_mailing_address'
                          labelText='Address'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.c5_care_provider_info_mailing_address}
                          maxLength={MAILING_ADDRESS_MAX_LENGTH}
                        />
                      </ColStyled>
                    </Row>
                    <Row>
                      <ColStyled medium={4}>
                        <TextField
                          id='c6_care_provider_info_mailing_address_city'
                          name='c6_care_provider_info_mailing_address_city'
                          labelText='City'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.c6_care_provider_info_mailing_address_city}
                          maxLength={CITY_NAME_MAX_LENGTH}
                        />
                      </ColStyled>
                      <ColStyled medium={4}>
                        <Select
                          id='c7_care_provider_info_mailing_address_state'
                          name='c7_care_provider_info_mailing_address_state'
                          labelText='State'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.c7_care_provider_info_mailing_address_state}
                          options={stateOptionsList}
                          maxLength={2}
                        />
                      </ColStyled>
                      <ColStyled
                        medium={4}
                        lastItem>
                        <TextField
                          id='c8_care_provider_info_mailing_address_zip'
                          name='c8_care_provider_info_mailing_address_zip'
                          labelText='ZIP Code'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          value={values.c8_care_provider_info_mailing_address_zip}
                          hasError={errors.c8_care_provider_info_mailing_address_zip}
                          helperText={setErrorMessage(errors.c8_care_provider_info_mailing_address_zip, 'Please enter a valid ZIP code.')}
                          maxLength={ZIP_MAX_LENGTH}
                        />
                      </ColStyled>
                    </Row>
                  </fieldset> {/* END PRIMARY CARE PROVIDER MAILING ADDRESS */}
                  <fieldset id='ltc-services-provider-information'> {/* START FORMAL SERVICES RADIO GROUP */}
                    <LegendStyled>
                      Formal Services Information
                    </LegendStyled>
                    <Row>
                      <Col>
                        <Typography
                          component='h3'
                          nextElement='typography'>
                          Formal Care Services
                        </Typography>
                      </Col>
                    </Row>
                    <Row>
                      <ColStyled lastItem>
                        <RadioGroup
                          id='d1_have_any_formal_care_services_been_provided'
                          name='d1_have_any_formal_care_services_been_provided'
                          labelText={<>Have any formal care services been provided&#63;</>}
                          onBlur={analyticsPush}
                          onChange={(e) => handleChange(e, 'ltc-services-provider-information')}
                          options={[
                            {
                              label: YES,
                              value: YES,
                            },
                            {
                              label: NO,
                              value: NO,
                            },
                          ]}
                        />
                      </ColStyled>
                    </Row>
                  </fieldset> {/* END  START FORMAL SERVICES RADIO GROUP */}
                  {/* //////////////////////////////////////////////////////////////////  */}
                  {/* START LTC SERVICES PROVIDER INFORMATION */}
                  {values.d1_have_any_formal_care_services_been_provided === YES && (
                    <fieldset>
                      <LegendStyled>
                        LTC Services Provider
                      </LegendStyled>
                      <Row>
                        <Col>
                          <Typography
                            component='h3'
                            nextElement='typography'>
                            LTC Services Provider
                          </Typography>
                        </Col>
                      </Row>
                      <Row>
                        <ColStyled>
                          <TextField
                            id='d4_ltc_service_provider_organization_name'
                            name='d4_ltc_service_provider_organization_name'
                            labelText='Organization Name'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.d4_ltc_service_provider_organization_name}
                            maxLength={35}
                          />
                        </ColStyled>
                        <ColStyled medium={6}>
                          <TextField
                            id='d2_ltc_service_provider_first_name'
                            name='d2_ltc_service_provider_first_name'
                            labelText='First Name'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.d2_ltc_service_provider_first_name}
                            hasError={errors.d2_ltc_service_provider_first_name}
                            helperText={setErrorMessage(errors.d2_ltc_service_provider_first_name, 'Please enter a valid first name.')}
                            maxLength={NAME_FIELD_MAX_LENGTH}
                          />
                        </ColStyled>
                        <ColStyled medium={6}>
                          <TextField
                            id='d3_ltc_service_provider_last_name'
                            name='d3_ltc_service_provider_last_name'
                            labelText='Last Name'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.d3_ltc_service_provider_last_name}
                            hasError={errors.d3_ltc_service_provider_last_name}
                            helperText={setErrorMessage(errors.d3_ltc_service_provider_last_name, 'Please enter a valid last name.')}
                            maxLength={LAST_NAME_FIELD_MAX_LENGTH}
                          />
                        </ColStyled>
                      </Row>
                      <Row>
                        <ColStyled
                          medium={6}
                          lastItem>
                          <TextField
                            id='d5_ltc_service_provider_phone'
                            name='d5_ltc_service_provider_phone'
                            type='tel'
                            labelText='Phone Number'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            onKeyUp={handlePhoneKeyUp}
                            value={values.d5_ltc_service_provider_phone}
                            hasError={errors.d5_ltc_service_provider_phone}
                            helperText={setErrorMessage(errors.d5_ltc_service_provider_phone, 'Please enter a valid phone number.')}
                            maxLength={PHONE_MAX_LENGTH}
                          />
                        </ColStyled>
                      </Row>
                      <fieldset> {/* START LTC SERVICES PROVIDER MAILING ADDRESS */}
                        <LegendStyled>
                          LTC Services Provider Mailing address
                        </LegendStyled>
                        <Row>
                          <Col medium={6}>
                            <Typography
                              component='h3'
                              nextElement='typography'>
                              Mailing Address
                            </Typography>
                          </Col>
                        </Row>
                        <Row>
                          <ColStyled medium={6}>
                            <TextField
                              id='d6_ltc_service_provider_mailing_address'
                              name='d6_ltc_service_provider_mailing_address'
                              labelText='Address'
                              onBlur={analyticsPush}
                              onChange={handleChange}
                              value={values.d6_ltc_service_provider_mailing_address}
                              maxLength={MAILING_ADDRESS_MAX_LENGTH}
                            />
                          </ColStyled>
                        </Row>
                        <Row>
                          <ColStyled medium={4}>
                            <TextField
                              id='d7_ltc_service_provider_mailing_address_city'
                              name='d7_ltc_service_provider_mailing_address_city'
                              labelText='City'
                              onBlur={analyticsPush}
                              onChange={handleChange}
                              value={values.d7_ltc_service_provider_mailing_address_city}
                              maxLength={CITY_NAME_MAX_LENGTH}
                            />
                          </ColStyled>
                          <ColStyled medium={4}>
                            <Select
                              id='d8_ltc_service_provider_mailing_address_state'
                              name='d8_ltc_service_provider_mailing_address_state'
                              labelText='State'
                              onBlur={analyticsPush}
                              onChange={handleChange}
                              value={values.d8_ltc_service_provider_mailing_address_state}
                              options={stateOptionsList}
                              maxLength={2}
                            />
                          </ColStyled>
                          <ColStyled
                            medium={4}
                            lastItem>
                            <TextField
                              id='d9_ltc_service_provider_mailing_address_zip'
                              name='d9_ltc_service_provider_mailing_address_zip'
                              labelText='ZIP Code'
                              onBlur={analyticsPush}
                              onChange={handleChange}
                              value={values.d9_ltc_service_provider_mailing_address_zip}
                              hasError={errors.d9_ltc_service_provider_mailing_address_zip}
                              helperText={setErrorMessage(errors.d9_ltc_service_provider_mailing_address_zip, 'Please enter a valid ZIP code.')}
                              maxLength={ZIP_MAX_LENGTH}
                            />
                          </ColStyled>
                        </Row>
                      </fieldset> {/* END PRIMARY CARE PROVIDER MAILING ADDRESS */}
                    </fieldset>
                  )} {/* END LTC SERVICES PROVIDER INFORMATION */}
                </fieldset> {/* END LONG TERM CARE INFORMATION ////////////////////////////////////////////////////////////////// */}
                {/*  ////////////////////////////////////////////////////////////////// */}
                <fieldset> {/* START OTHER INSURANCE  */}
                  <LegendStyled>
                    Other Insurance
                  </LegendStyled>
                  <Row>
                    <Col>
                      <Typography
                        component='h2'
                        nextElement='typography'>
                        Other Insurance
                      </Typography>
                    </Col>
                  </Row>
                  <fieldset> {/* START OTHER LTC INSURANCE RADIO GROUP */}
                    <LegendStyled>
                      Other LTC Insurance
                    </LegendStyled>
                    <Row>
                      <ColStyled>
                        <RadioGroup
                          id='e1_insured_has_other_ltc_insurance'
                          name='e1_insured_has_other_ltc_insurance'
                          labelText='Indicate if Insured has any other LTC Insurance'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          options={[
                            {
                              label: YES,
                              value: YES,
                            },
                            {
                              label: NO,
                              value: NO,
                              // selected: true,
                            },
                          ]}
                        />
                      </ColStyled>
                    </Row>
                  </fieldset> {/* END OTHER LTC INSURANCE RADIO GROUP */}
                  <fieldset> {/* START OTHER LTC INSURANCE RADIO GROUP */}
                    <LegendStyled>
                      Northwestern Mutual Group Disability Insurance
                    </LegendStyled>
                    <Row>
                      <ColStyled>
                        <RadioGroup
                          id='e2_insured_has_nm_group_di_insurance'
                          name='e2_insured_has_nm_group_di_insurance'
                          labelText='Indicate if Insured has Northwestern Mutual Group Disability Insurance:'
                          onBlur={analyticsPush}
                          onChange={handleChange}
                          options={[
                            {
                              label: YES,
                              value: YES,
                            },
                            {
                              label: NO,
                              value: NO,
                            },
                          ]}
                        />
                      </ColStyled>
                    </Row>
                  </fieldset> {/* END OTHER LTC INSURANCE RADIO GROUP */}
                </fieldset> {/* END OTHER INSURANCE  */}
                {/*  ////////////////////////////////////////////////////////////////// */}
                <fieldset> {/* START LONG TERM CARE NOTIFICATION COMPLETED BY  */}
                  <LegendStyled>
                    Long Term Care Notification Completed by
                  </LegendStyled>
                  <Row>
                    <Col>
                      <Typography
                        component='h2'
                        nextElement='typography'>
                        Long Term Care Notification <WidowBlock>Completed by</WidowBlock>
                      </Typography>
                    </Col>
                  </Row>
                  <fieldset id='long-term-care-completed-by'> {/* START NOTIFICATION COMPLETED BY RADIO GROUP */}
                    <LegendStyled>
                      Notification completed by
                    </LegendStyled>
                    <Row>
                      <ColStyled>
                        <RadioGroup
                          id='f1_ltc_notification_completed_by'
                          name='f1_ltc_notification_completed_by'
                          labelText={<>Notification Completed by&#42;</>}
                          onBlur={analyticsPush}
                          onChange={(e) => handleChange(e, 'long-term-care-completed-by')}
                          hasError={errors.f1_ltc_notification_completed_by}
                          helperText={setErrorMessage(errors.f1_ltc_notification_completed_by, 'Please select an option.')}
                          options={[
                            {
                              label: INSURED,
                              value: INSURED,
                            },
                            {
                              label: INSUREDS_REPRESENTATIVE,
                              value: INSUREDS_REPRESENTATIVE,
                            },
                            {
                              label: INSUREDS_FR,
                              value: INSUREDS_FR,
                            },
                          ]}
                        />
                      </ColStyled>
                    </Row>
                  </fieldset> {/* END NOTIFICATION COMPLETED BY RADIO GROUP */}
                  {/* conditional inputs, if completed by insured's representative */}
                  {values.f1_ltc_notification_completed_by === INSUREDS_REPRESENTATIVE && (
                    <>
                      <Row>
                        <ColStyled medium={6}>
                          <TextField
                            id='g1_insured_rep_name'
                            name='g1_insured_rep_name'
                            labelText='Please provide your name'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.g1_insured_rep_name}
                            hasError={errors.g1_insured_rep_name}
                            helperText={setErrorMessage(errors.g1_insured_rep_name, 'Please enter a valid name.')}
                            maxLength={70}
                          />
                        </ColStyled>
                        <ColStyled medium={6}>
                          <TextField
                            id='g2_insured_rep_phone'
                            name='g2_insured_rep_phone'
                            type='tel'
                            labelText='Phone Number'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            onKeyUp={handlePhoneKeyUp}
                            value={values.g2_insured_rep_phone}
                            hasError={errors.g2_insured_rep_phone}
                            helperText={setErrorMessage(errors.g2_insured_rep_phone, 'Please enter a valid phone number.')}
                            maxLength={PHONE_MAX_LENGTH}
                          />
                        </ColStyled>
                        <ColStyled
                          medium={6}
                          lastItem>
                          <TextField
                            id='g3_insured_rep_relationship'
                            name='g3_insured_rep_relationship'
                            labelText='Please provide your relationship to insured'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.g3_insured_rep_relationship}
                            maxLength={150}
                          />
                        </ColStyled>
                      </Row>
                    </>
                  )}
                  {/* conditional inputs, if completed by insured's financial rep */}
                  {values.f1_ltc_notification_completed_by === INSUREDS_FR && (
                    <>
                      <Row>
                        <ColStyled medium={6}>
                          <TextField
                            id='h1_fr_or_afr_name'
                            name='h1_fr_or_afr_name'
                            labelText='Please provide your name'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.h1_fr_or_afr_name}
                            hasError={errors.h1_fr_or_afr_name}
                            helperText={setErrorMessage(errors.h1_fr_or_afr_name, 'Please enter a valid name.')}
                            maxLength={70}
                          />
                        </ColStyled>
                        <ColStyled medium={6}>
                          <TextField
                            id='h2_fr_or_afr_number'
                            name='h2_fr_or_afr_number'
                            labelText={<>Please provide your FR&#47;AFR Number</>}
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            value={values.h2_fr_or_afr_number}
                            maxLength={150}
                          />
                        </ColStyled>
                        <ColStyled medium={6}>
                          <TextField
                            id='h3_fr_or_afr_phone'
                            name='h3_fr_or_afr_phone'
                            type='tel'
                            labelText='Phone Number'
                            onBlur={analyticsPush}
                            onChange={handleChange}
                            onKeyUp={handlePhoneKeyUp}
                            value={values.h3_fr_or_afr_phone}
                            hasError={errors.h3_fr_or_afr_phone}
                            helperText={setErrorMessage(errors.h3_fr_or_afr_phone, 'Please enter a valid phone number.')}
                            maxLength={PHONE_MAX_LENGTH}
                          />
                        </ColStyled>
                        <ColStyled medium={6}>
                          <TextField
                            id='h4_fr_or_afr_notifed_date'
                            name='h4_fr_or_afr_notifed_date'
                            type='date'
                            max={currentDate}
                            maxLength={DATE_MAX_LENGTH}
                            labelText='Please provide the date you were notified'
                            onBlur={analyticsPush}
                            onChange={handleDateChange}
                            hasError={errors.h4_fr_or_afr_notifed_date}
                            helperText={setErrorMessage(errors.h4_fr_or_afr_notifed_date, 'Please enter a valid date.')}
                            required={values.f1_ltc_notification_completed_by === INSUREDS_FR}
                          />
                        </ColStyled>
                      </Row>
                    </>
                  )}
                </fieldset>{/* END LONG TERM CARE NOTIFICATION COMPLETED BY  */}
                {/* SUBMIT BUTTON ////////////////////////////////////////////// */}
                <Row>
                  <Col>
                    <Button
                      variant='primary'
                      type='submit'
                      id='notice-of-long-term-care-form-submit-button'
                      ariaLabel='Submit Button'
                      disabled={isSubmitting}>
                      Submit
                    </Button>
                  </Col>
                </Row>
                <input
                  type='hidden'
                  name='recaptcha_response'
                  className='recaptchaResponse' />
              </FormStyled>
            </Col>
          </Row>
        </>
      )}

      {/* SHOW LOADING ICON */}
      {showLoading && (
        <Row align='center'>
          <Col>
            <Loading />
          </Col>
        </Row>
      )}

      {/* THANK YOU SCREEN */}
      {showSuccess && (
        <Row align='center'>
          <Col
            large={10}
            xlarge={8}>
            <SubmissionSuccess
              headingText={<>We&#39;ve received the claim notification and are here to help during this difficult time.</>}
              descriptionText={<>
                <Typography
                  block
                  component='span'
                  nextElement='typography'>
                  The request for Long&#45;Term Care Benefits was received in the Claims Department of Northwestern Mutual. A claims analyst will contact you to set up an interview.
                </Typography>
                <Typography
                  block
                  component='span'
                  disableBottomPadding>
                  If you have any questions, call Client Services at{' '}
                  <Link
                    id='client-services-phone-link-thank-you'
                    variant='primaryDeemphasize'
                    linkUrl='tel:8007489493'>
                    &#40;800&#41; 748&#45;9493
                  </Link>
                  , <ClientServicesHours7to5 />.
                </Typography>
              </>}
            />
          </Col>
        </Row>
      )}

      {/* SHOW ERROR SCREEN */}
      {showError && (
        <Row align='center'>
          <Col
            large={10}
            xlarge={8}>
            <SubmissionError
              headingText='Unable to submit form.'
              descriptionText='Please try again later.'
            />
          </Col>
        </Row>
      )}
    </SectionContainer>
  );
};

